import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "phoneNumber",
    "smsAuthorizationRadio",
    "smsPhoneNumber",
    "submitButton",
    "submitIcon",
    "submitLoadingIcon",
  ]

  connect() {
    this.isLoading = false
    this.pendingUploads = 0
    this.turnstilePending = this.element.dataset.turnstilePending === "true"
    this.element.addEventListener("FilePond:processfile", this.handleFilePondProcessed)
    // processfilestart (not addfilestart) so every increment belongs to an
    // actual processing attempt: a file that fails loading or validation never
    // reaches processfile and would leave the counter stuck positive.
    this.element.addEventListener("FilePond:processfilestart", this.handleFilePondProcessing)
    this.element.addEventListener("submit", this.handleSubmit)
    this.element.addEventListener("turnstile:state-change", this.handleTurnstileStateChange)
    this.updateSmsPhoneRequired()
    this.updateSubmitDisabled()
  }

  disconnect() {
    this.element.removeEventListener("FilePond:processfile", this.handleFilePondProcessed)
    this.element.removeEventListener("FilePond:processfilestart", this.handleFilePondProcessing)
    this.element.removeEventListener("submit", this.handleSubmit)
    this.element.removeEventListener("turnstile:state-change", this.handleTurnstileStateChange)
  }

  handleFilePondProcessed = () => {
    this.pendingUploads = Math.max(0, this.pendingUploads - 1)
    this.updateSubmitDisabled()
  }

  handleFilePondProcessing = () => {
    this.pendingUploads++
    this.updateSubmitDisabled()
  }

  handleSubmit = (_e) => {
    this.toggleIsLoading()
  }

  handleTurnstileStateChange = ({ detail }) => {
    this.turnstilePending = detail.pending === true
    this.updateSubmitDisabled()
  }

  toggleIsLoading() {
    this.isLoading = !this.isLoading

    if (this.isLoading) {
      this.submitIconTarget.classList.add("hidden")
      this.submitLoadingIconTarget.classList.remove("hidden")
    } else {
      this.submitIconTarget.classList.remove("hidden")
      this.submitLoadingIconTarget.classList.add("hidden")
    }

    this.updateSubmitDisabled()
  }

  updateSubmitDisabled() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled =
        this.isLoading || this.pendingUploads > 0 || this.turnstilePending
    }
  }

  onSmsAuthorizationChange() {
    this.updateSmsPhoneRequired()
    this.autofillSmsPhoneNumber()
  }

  // Convenience for sequential form-fillers: when the customer opts into SMS,
  // pre-fill the SMS phone with the phone they already entered above. Only
  // fills an empty SMS field so we never clobber a manual correction, and does
  // nothing if the phone above is still blank.
  autofillSmsPhoneNumber() {
    if (!this.hasPhoneNumberTarget || !this.hasSmsPhoneNumberTarget) {
      return
    }

    const yesRadio = this.smsAuthorizationRadioTargets.find(
      (radio) => radio.value === "true" || radio.value === true
    )

    if (!yesRadio || !yesRadio.checked) {
      return
    }

    if (this.smsPhoneNumberTarget.value.trim() !== "") {
      return
    }

    const phone = this.phoneNumberTarget.value.trim()

    if (phone !== "") {
      this.smsPhoneNumberTarget.value = phone
    }
  }

  updateSmsPhoneRequired() {
    if (!this.hasSmsPhoneNumberTarget || !this.smsAuthorizationRadioTargets) {
      return
    }

    const yesRadio = this.smsAuthorizationRadioTargets.find(
      (radio) => radio.value === "true" || radio.value === true
    )

    if (yesRadio && yesRadio.checked) {
      this.smsPhoneNumberTarget.setAttribute("required", "true")
    } else {
      this.smsPhoneNumberTarget.removeAttribute("required")
    }
  }
}
